import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ActionIcon, Alert, Button, Card, Divider, Group, Loader, Modal, SimpleGrid, Stack, Text, TextInput, ThemeIcon, Title, Tooltip } from '@mantine/core'
import { IconAlertCircle, IconArrowRight, IconCheck, IconCreditCard, IconExternalLink, IconGift, IconPhoto, IconSparkles, IconVideo } from '@tabler/icons-react'
import { api } from '../api/client'
import { getApiErrorCode } from '../api/errors'
import { useAuth } from '../auth/AuthContext'
import { useToast } from '../components/Toast'
import { enqueuePaymentAnalytics } from './paymentAnalyticsOutbox'
import { getYandexCheckoutAttribution } from '../analytics/yandexMetrika'

type BillingPack = {
  code: string
  title: string
  credits: number
  priceMinor: number
  oldPriceMinor?: number | null
  currency: string
  paymentDisabledReason?: string | null
  foreignCardPaymentDisabledReason?: string | null
}

type PromoValidationResult = {
  valid: boolean
  discountPercent: number
  code: string
  partnerCode?: boolean
  reason?: string | null
  alreadyUsed?: boolean
  firstTopupRequired?: boolean
  partnerConflict?: boolean
}

type ReferralTopupStatus = {
  hasPaidTopups: boolean
}

type TelegramRewardStatus = {
  enabled: boolean
  linked: boolean
  subscribed: boolean
  rewardGranted: boolean
  rewardCredits: number
}

type PendingPaymentRecord = {
  paymentId: number
  providerPaymentId?: string
  provider?: 'yookassa' | 'robokassa' | string
  amountMinor?: number
  currency?: string
  credits?: number
  packCode?: string
  packTitle?: string
  createdAtMs?: number
  source?: string | null
}

type OpenTopupOptions = {
  requiredCredits?: number
  currentBalanceCredits?: number | null
  source?: string
}

type BillingFlowContextValue = {
  openTopup: (options?: OpenTopupOptions) => void
  pendingPaymentsCount: number
}

const BillingFlowContext = createContext<BillingFlowContextValue | null>(null)

const PENDING_PAYMENTS_KEY = 'billing.pending_payments_v2'
const LEGACY_PENDING_PAYMENT_KEY = 'billing.pending_topup_payment'
const PAID_SEEN_KEY = 'billing.paid_seen_v1'
const RECEIPT_EMAIL_KEY = 'billing.receipt_email'
const POLL_INTERVAL_MS = 5000
const POLL_MAX_ITEMS = 2
const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i

const isTerminalPaymentStatus = (statusRaw: string) => {
  const status = String(statusRaw || '').toUpperCase()
  return status === 'PAID' || status === 'FAILED' || status === 'CANCELED'
}

const formatCurrency = (minor: number, currency = 'RUB') => {
  const value = (Number.isFinite(minor) ? minor : 0) / 100
  try {
    return value.toLocaleString('ru-RU', { style: 'currency', currency, maximumFractionDigits: 0 })
  } catch {
    return `${Math.round(value)} ${currency}`
  }
}

const normalizeReceiptEmail = (raw?: string | null) => String(raw || '').trim().toLowerCase()

const isReceiptEmailValid = (raw?: string | null) => {
  const email = normalizeReceiptEmail(raw)
  return email.length >= 6 && email.length <= 254 && EMAIL_REGEX.test(email)
}

const initialReceiptEmail = (profileEmail?: string | null) => {
  const accountEmail = normalizeReceiptEmail(profileEmail)
  if (isReceiptEmailValid(accountEmail)) return accountEmail
  if (typeof window === 'undefined') return ''
  const savedEmail = normalizeReceiptEmail(window.localStorage.getItem(RECEIPT_EMAIL_KEY))
  return isReceiptEmailValid(savedEmail) ? savedEmail : ''
}

const missingCreditsForTopup = (requiredCredits?: number, balanceCredits?: number | null) => {
  const required = Number(requiredCredits)
  if (!Number.isFinite(required) || required <= 0) return null
  const balance = Number(balanceCredits ?? 0)
  return Math.max(0, required - (Number.isFinite(balance) ? balance : 0))
}

const getPackDescription = (code: string) => {
  const normalized = String(code || '').toLowerCase()
  if (normalized === 'basic') return 'Лёгкий старт'
  if (normalized === 'plus') return 'Поток контента'
  if (normalized === 'pro') return 'Полный контроль'
  if (normalized === 'ultra') return 'Для масштаба'
  return 'Пакет кредитов'
}

const getPackMarketingBadge = (code: string) => {
  const normalized = String(code || '').toLowerCase()
  if (normalized === 'plus') {
    return {
      label: 'ХИТ ПРОДАЖ',
      background: 'linear-gradient(135deg, #ff7a18 0%, #ff3d7f 100%)'
    }
  }
  if (normalized === 'pro') {
    return {
      label: 'ЛУЧШИЙ ВЫБОР',
      background: 'linear-gradient(135deg, #00c853 0%, #00bfa5 100%)'
    }
  }
  return null
}

const readPendingPayments = (): PendingPaymentRecord[] => {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(PENDING_PAYMENTS_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        return parsed
          .map((entry) => {
            const paymentId = Number(entry?.paymentId)
            if (!Number.isFinite(paymentId) || paymentId <= 0) return null
            return {
              paymentId: Math.trunc(paymentId),
              providerPaymentId: typeof entry?.providerPaymentId === 'string' ? entry.providerPaymentId : undefined,
              amountMinor: Number.isFinite(Number(entry?.amountMinor)) ? Number(entry.amountMinor) : undefined,
              currency: typeof entry?.currency === 'string' ? entry.currency : undefined,
              credits: Number.isFinite(Number(entry?.credits)) ? Number(entry.credits) : undefined,
              packCode: typeof entry?.packCode === 'string' ? entry.packCode : undefined,
              packTitle: typeof entry?.packTitle === 'string' ? entry.packTitle : undefined,
              createdAtMs: Number.isFinite(Number(entry?.createdAtMs)) ? Number(entry.createdAtMs) : Date.now(),
              source: typeof entry?.source === 'string' ? entry.source : null
            } satisfies PendingPaymentRecord
          })
          .filter(Boolean) as PendingPaymentRecord[]
      }
    }
  } catch {
    // ignore
  }

  try {
    const legacyRaw = window.localStorage.getItem(LEGACY_PENDING_PAYMENT_KEY)
    if (!legacyRaw) return []
    const legacy = JSON.parse(legacyRaw)
    const paymentId = Number(legacy?.paymentId)
    if (!Number.isFinite(paymentId) || paymentId <= 0) return []
    return [{
      paymentId: Math.trunc(paymentId),
      providerPaymentId: typeof legacy?.providerPaymentId === 'string' ? legacy.providerPaymentId : undefined,
      amountMinor: Number.isFinite(Number(legacy?.amountMinor)) ? Number(legacy.amountMinor) : undefined,
      currency: typeof legacy?.currency === 'string' ? legacy.currency : undefined,
      credits: Number.isFinite(Number(legacy?.credits)) ? Number(legacy.credits) : undefined,
      packCode: typeof legacy?.packCode === 'string' ? legacy.packCode : undefined,
      packTitle: typeof legacy?.packTitle === 'string' ? legacy.packTitle : undefined,
      createdAtMs: Number.isFinite(Number(legacy?.createdAtMs)) ? Number(legacy.createdAtMs) : Date.now(),
      source: null
    }]
  } catch {
    return []
  }
}

const writePendingPayments = (items: PendingPaymentRecord[]) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(PENDING_PAYMENTS_KEY, JSON.stringify(items))
    if (items.length > 0) {
      window.localStorage.setItem(LEGACY_PENDING_PAYMENT_KEY, JSON.stringify(items[0]))
    } else {
      window.localStorage.removeItem(LEGACY_PENDING_PAYMENT_KEY)
    }
  } catch {
    // ignore
  }
}

const readSeenPaidIds = () => {
  if (typeof window === 'undefined') return new Set<number>()
  try {
    const raw = window.localStorage.getItem(PAID_SEEN_KEY)
    if (!raw) return new Set<number>()
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set<number>()
    return new Set(
      parsed
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item) && item > 0)
        .map((item) => Math.trunc(item))
    )
  } catch {
    return new Set<number>()
  }
}

const writeSeenPaidIds = (seen: Set<number>) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(PAID_SEEN_KEY, JSON.stringify(Array.from(seen)))
  } catch {
    // ignore
  }
}

export function BillingFlowProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const { hydrated, isAuthed, profile, refreshProfile } = useAuth()
  const { showSuccess } = useToast()

  const [opened, setOpened] = useState(false)
  const [packs, setPacks] = useState<BillingPack[]>([])
  const [packsLoading, setPacksLoading] = useState(false)
  const [packsError, setPacksError] = useState<string | null>(null)
  const [selectedPackCode, setSelectedPackCode] = useState<string | null>(null)

  const [promoCode, setPromoCode] = useState('')
  const [promoValidation, setPromoValidation] = useState<PromoValidationResult | null>(null)
  const [promoValidating, setPromoValidating] = useState(false)
  const [promoError, setPromoError] = useState<string | null>(null)

  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [checkoutProvider, setCheckoutProvider] = useState<'yookassa' | 'robokassa' | null>(null)
  const [checkoutPackCode, setCheckoutPackCode] = useState<string | null>(null)
  const [receiptEmail, setReceiptEmail] = useState('')
  const [receiptEmailError, setReceiptEmailError] = useState<string | null>(null)
  const [foreignCardTooltipTarget, setForeignCardTooltipTarget] = useState<string | null>(null)
  const [lastConfirmationUrl, setLastConfirmationUrl] = useState<string | null>(null)
  const [processingPaymentId, setProcessingPaymentId] = useState<number | null>(null)
  const [topupStep, setTopupStep] = useState<'packs' | 'checkout' | 'waiting'>('packs')
  const [topupSessionKey, setTopupSessionKey] = useState(0)
  const [waitingPaymentStatus, setWaitingPaymentStatus] = useState<'pending' | 'failed' | 'canceled'>('pending')
  const [waitingPaymentRecord, setWaitingPaymentRecord] = useState<PendingPaymentRecord | null>(null)
  const [referralTopupStatus, setReferralTopupStatus] = useState<ReferralTopupStatus | null>(null)
  const [telegramRewardStatus, setTelegramRewardStatus] = useState<TelegramRewardStatus | null>(null)

  const [pendingPayments, setPendingPayments] = useState<PendingPaymentRecord[]>(() => readPendingPayments())
  const [successPayment, setSuccessPayment] = useState<PendingPaymentRecord | null>(null)
  const pendingPaymentsRef = useRef<PendingPaymentRecord[]>(pendingPayments)
  const paidSeenRef = useRef<Set<number>>(readSeenPaidIds())
  const inFlightPollRef = useRef(false)
  const successQueueRef = useRef<PendingPaymentRecord[]>([])
  const receiptEmailTouchedRef = useRef(false)
  const requiredCreditsHintRef = useRef<number | null>(null)
  const foreignCardTooltipTimerRef = useRef<number | null>(null)

  useEffect(() => { pendingPaymentsRef.current = pendingPayments }, [pendingPayments])

  useEffect(() => {
    if (receiptEmailTouchedRef.current) return
    const initial = initialReceiptEmail(profile?.email)
    if (initial && receiptEmail !== initial) setReceiptEmail(initial)
  }, [profile?.email, receiptEmail])

  const selectedPack = useMemo(
    () => packs.find((entry) => entry.code === selectedPackCode) || null,
    [packs, selectedPackCode]
  )

  const showTelegramBonusHint = useMemo(() => {
    if (!referralTopupStatus || !telegramRewardStatus) return false
    if (!telegramRewardStatus.enabled || Number(telegramRewardStatus.rewardCredits || 0) <= 0) return false
    if (referralTopupStatus.hasPaidTopups) return false
    if (telegramRewardStatus.linked) return false
    return !telegramRewardStatus.subscribed && !telegramRewardStatus.rewardGranted
  }, [referralTopupStatus, telegramRewardStatus])

  const telegramBonusCreditsLabel = useMemo(() => {
    const credits = Number(telegramRewardStatus?.rewardCredits || 0)
    if (!Number.isFinite(credits) || credits <= 0) return ''
    return Number.isInteger(credits) ? String(credits) : credits.toFixed(2)
  }, [telegramRewardStatus?.rewardCredits])

  const paymentDisabledReason = useMemo(
    () => selectedPack?.paymentDisabledReason?.trim() || '',
    [selectedPack?.paymentDisabledReason]
  )

  const foreignCardDisabledReason = useMemo(
    () => selectedPack?.foreignCardPaymentDisabledReason?.trim() || '',
    [selectedPack?.foreignCardPaymentDisabledReason]
  )

  const discountMinor = useMemo(() => {
    const price = Number(selectedPack?.priceMinor || 0)
    const validDiscount = promoValidation?.valid ? Number(promoValidation.discountPercent || 0) : 0
    if (price <= 0 || validDiscount <= 0) return 0
    return Math.round(price * validDiscount / 100)
  }, [promoValidation?.discountPercent, promoValidation?.valid, selectedPack?.priceMinor])

  const finalPriceMinor = useMemo(() => {
    const price = Number(selectedPack?.priceMinor || 0)
    return Math.max(0, price - discountMinor)
  }, [discountMinor, selectedPack?.priceMinor])

  const savingsMinor = useMemo(() => {
    const price = Number(selectedPack?.priceMinor || 0)
    const oldPrice = Number(selectedPack?.oldPriceMinor || 0)
    const baseComparablePrice = Math.max(price, oldPrice)
    if (baseComparablePrice <= 0) return 0
    return Math.max(0, baseComparablePrice - finalPriceMinor)
  }, [finalPriceMinor, selectedPack?.oldPriceMinor, selectedPack?.priceMinor])

  const processingPayment = useMemo(
    () => pendingPayments.find((entry) => entry.paymentId === processingPaymentId) || null,
    [pendingPayments, processingPaymentId]
  )

  const mergePendingPayments = useCallback((current: PendingPaymentRecord[], incoming: PendingPaymentRecord[]) => {
    const byId = new Map<number, PendingPaymentRecord>()
    current.forEach((entry) => byId.set(entry.paymentId, entry))
    incoming.forEach((entry) => {
      const prev = byId.get(entry.paymentId)
      byId.set(entry.paymentId, {
        ...prev,
        ...entry,
        paymentId: entry.paymentId
      })
    })
    return Array.from(byId.values()).sort((a, b) => Number(b.createdAtMs || 0) - Number(a.createdAtMs || 0))
  }, [])

  const enqueueSuccessModal = useCallback((payment: PendingPaymentRecord) => {
    const id = Math.trunc(Number(payment.paymentId))
    if (!Number.isFinite(id) || id <= 0) return
    if (paidSeenRef.current.has(id)) return
    paidSeenRef.current.add(id)
    writeSeenPaidIds(paidSeenRef.current)
    if (!successPayment) {
      setSuccessPayment(payment)
      return
    }
    successQueueRef.current.push(payment)
  }, [successPayment])

  const closeSuccessModal = useCallback(() => {
    const next = successQueueRef.current.shift() || null
    setSuccessPayment(next)
  }, [])

  const loadPacks = useCallback(async () => {
    setPacksLoading(true)
    setPacksError(null)
    try {
      const response = await api.listCreditPacks('RUB')
      setPacks(response)
      setSelectedPackCode((prev) => {
        if (prev && response.some((pack) => pack.code === prev)) return prev
        const requiredCredits = Number(requiredCreditsHintRef.current || 0)
        if (requiredCredits > 0) {
          const matched = response
            .filter((pack) => Number(pack.credits) >= requiredCredits)
            .sort((a, b) => Number(a.credits) - Number(b.credits))[0]
          if (matched?.code) return matched.code
        }
        return response[0]?.code || null
      })
    } catch {
      setPacksError('Не удалось загрузить пакеты.')
    } finally {
      setPacksLoading(false)
    }
  }, [])

  const loadTopupBonusEligibility = useCallback(async () => {
    try {
      const [topupStatus, telegramStatus] = await Promise.all([
        api.getReferralTopupStatus(),
        api.getTelegramStatus()
      ])
      setReferralTopupStatus({
        hasPaidTopups: Boolean(topupStatus?.hasPaidTopups)
      })
      setTelegramRewardStatus({
        enabled: Boolean(telegramStatus?.enabled),
        linked: Boolean(telegramStatus?.linked),
        subscribed: Boolean(telegramStatus?.subscribed),
        rewardGranted: Boolean(telegramStatus?.rewardGranted),
        rewardCredits: Number(telegramStatus?.rewardCredits || 0)
      })
    } catch {
      setReferralTopupStatus(null)
      setTelegramRewardStatus(null)
    }
  }, [])

  const applyPromo = useCallback(async () => {
    const normalizedCode = promoCode.trim()
    if (!normalizedCode) {
      setPromoValidation(null)
      setPromoError(null)
      return
    }
    setPromoValidating(true)
    setPromoError(null)
    try {
      const result = await api.validatePromoCode(normalizedCode)
      if (!result.valid) {
        setPromoValidation(null)
        setPromoError('Промокод недействителен или истек.')
        return
      }
      setPromoValidation({
        valid: true,
        discountPercent: result.discountPercent,
        code: normalizedCode,
        partnerCode: result.partnerCode
      })
      showSuccess(`Промокод применен: -${result.discountPercent}%`)
    } catch {
      setPromoValidation(null)
      setPromoError('Не удалось проверить промокод.')
    } finally {
      setPromoValidating(false)
    }
  }, [promoCode, showSuccess])

  const openConfirmationInNewTab = useCallback((urlRaw: string) => {
    const url = String(urlRaw || '').trim()
    if (!url) return false
    const target = window.open(url, '_blank', 'noopener,noreferrer')
    return !!target
  }, [])

  const submitCheckout = useCallback(async (provider: 'yookassa' | 'robokassa' = 'yookassa', packOverride?: BillingPack) => {
    const packForCheckout = packOverride || selectedPack
    if (!packForCheckout) return
    const normalizedPromo = promoCode.trim()
    const normalizedReceiptEmail = normalizeReceiptEmail(receiptEmail)
    if (!isReceiptEmailValid(normalizedReceiptEmail)) {
      setReceiptEmailError('Введите корректный email для отправки чека.')
      return
    }
    setReceiptEmailError(null)
    if (normalizedPromo && (!promoValidation || promoValidation.code !== normalizedPromo || !promoValidation.valid)) {
      setPromoError('Примените промокод перед оплатой.')
      return
    }
    const amountMinor = Math.max(
      0,
      Number(packForCheckout.priceMinor || 0) - (packForCheckout.code === selectedPack?.code ? discountMinor : 0)
    )
    setCheckoutLoading(true)
    setCheckoutProvider(provider)
    setCheckoutPackCode(packForCheckout.code)
    try {
      const attribution = await getYandexCheckoutAttribution()
      const checkout = await api.checkoutCreditPack(
        packForCheckout.code,
        'RUB',
        `${window.location.origin}/studio`,
        normalizedPromo || undefined,
        provider,
        normalizedReceiptEmail,
        attribution
      )
      window.localStorage.setItem(RECEIPT_EMAIL_KEY, normalizedReceiptEmail)
      const record: PendingPaymentRecord = {
        paymentId: checkout.paymentId,
        providerPaymentId: checkout.providerPaymentId,
        provider,
        amountMinor,
        currency: packForCheckout.currency,
        credits: packForCheckout.credits,
        packCode: packForCheckout.code,
        packTitle: packForCheckout.title,
        createdAtMs: Date.now(),
        source: 'modal'
      }
      enqueuePaymentAnalytics(record)
      setPendingPayments((prev) => {
        const next = mergePendingPayments(prev, [record])
        writePendingPayments(next)
        return next
      })
      setProcessingPaymentId(checkout.paymentId)
      setWaitingPaymentRecord(record)
      setWaitingPaymentStatus('pending')
      setLastConfirmationUrl(checkout.confirmationUrl)
      setTopupStep('waiting')
      openConfirmationInNewTab(checkout.confirmationUrl)
    } catch (error) {
      const code = getApiErrorCode(error).toLowerCase()
      if (code === 'payment_required' || code === 'not_enough_credits' || code.startsWith('http_402')) {
        setPromoError('Недостаточно кредитов для оплаты выбранного пакета.')
      } else {
        setPromoError(String((error as any)?.message || 'Не удалось создать платеж.'))
      }
    } finally {
      setCheckoutLoading(false)
      setCheckoutProvider(null)
      setCheckoutPackCode(null)
    }
  }, [discountMinor, mergePendingPayments, openConfirmationInNewTab, promoCode, promoValidation, receiptEmail, selectedPack])

  const showForeignCardDisabledTooltip = useCallback((target: string = 'checkout') => {
    setForeignCardTooltipTarget(target)
    if (foreignCardTooltipTimerRef.current) {
      window.clearTimeout(foreignCardTooltipTimerRef.current)
    }
    foreignCardTooltipTimerRef.current = window.setTimeout(() => {
      setForeignCardTooltipTarget(null)
      foreignCardTooltipTimerRef.current = null
    }, 2600)
  }, [])

  const hideForeignCardDisabledTooltip = useCallback((target: string = 'checkout') => {
    if (foreignCardTooltipTimerRef.current) {
      window.clearTimeout(foreignCardTooltipTimerRef.current)
      foreignCardTooltipTimerRef.current = null
    }
    setForeignCardTooltipTarget((current) => current === target ? null : current)
  }, [])

  const pollPendingPayments = useCallback(async () => {
    const visible = typeof document === 'undefined' ? true : document.visibilityState === 'visible'
    if (!visible) return
    const items = pendingPaymentsRef.current
    if (!items.length) return
    if (inFlightPollRef.current) return
    const pollTargets = [...items]
      .sort((a, b) => {
        const byCreatedAt = Number(b.createdAtMs || 0) - Number(a.createdAtMs || 0)
        if (byCreatedAt !== 0) return byCreatedAt
        return Number(b.paymentId || 0) - Number(a.paymentId || 0)
      })
      .slice(0, POLL_MAX_ITEMS)
    if (!pollTargets.length) return
    inFlightPollRef.current = true
    try {
      const statuses = await Promise.all(
        pollTargets.map(async (item) => {
          try {
            const status = await api.getBillingPaymentStatus(item.paymentId)
            return { item, status, failed: false as const }
          } catch (error) {
            return { item, status: null, failed: true as const, error }
          }
        })
      )

      const statusesByPaymentId = new Map<number, (typeof statuses)[number]>(
        statuses.map((entry) => [entry.item.paymentId, entry])
      )

      let profileNeedsRefresh = false
      const nextPending: PendingPaymentRecord[] = []

      items.forEach((item) => {
        const entry = statusesByPaymentId.get(item.paymentId)
        if (!entry) {
          nextPending.push(item)
          return
        }
        if (entry.failed || !entry.status) {
          nextPending.push(item)
          return
        }
        const status = String(entry.status.status || '').toUpperCase()
        if (status === 'PAID') {
          profileNeedsRefresh = true
          if (processingPaymentId === item.paymentId) {
            setProcessingPaymentId(null)
            setWaitingPaymentRecord(null)
            setWaitingPaymentStatus('pending')
            setTopupStep('packs')
            setOpened(false)
          }
          enqueueSuccessModal({
            ...item,
            amountMinor: entry.status.amountMinor,
            currency: entry.status.currency,
            credits: entry.status.credits,
            providerPaymentId: entry.status.providerPaymentId
          })
          return
        }
        if (isTerminalPaymentStatus(status)) {
          if (processingPaymentId === item.paymentId) {
            setWaitingPaymentRecord({
              ...item,
              amountMinor: entry.status.amountMinor,
              currency: entry.status.currency,
              credits: entry.status.credits,
              providerPaymentId: entry.status.providerPaymentId
            })
            setWaitingPaymentStatus(status === 'CANCELED' ? 'canceled' : 'failed')
            setProcessingPaymentId(null)
            setTopupStep('waiting')
          }
          return
        }
        const updatedRecord: PendingPaymentRecord = {
          ...item,
          amountMinor: entry.status.amountMinor,
          currency: entry.status.currency,
          credits: entry.status.credits,
          providerPaymentId: entry.status.providerPaymentId
        }
        if (processingPaymentId === item.paymentId) {
          setWaitingPaymentRecord(updatedRecord)
          setWaitingPaymentStatus('pending')
        }
        nextPending.push(updatedRecord)
      })

      setPendingPayments(nextPending)
      writePendingPayments(nextPending)

      if (profileNeedsRefresh) {
        await refreshProfile(true)
      }
    } finally {
      inFlightPollRef.current = false
    }
  }, [enqueueSuccessModal, processingPaymentId, refreshProfile])

  useEffect(() => {
    if (!hydrated || !isAuthed) return
    let cancelled = false
    const bootstrap = async () => {
      try {
        const response = await api.listPendingBillingPayments(30)
        if (cancelled) return
        const serverItems: PendingPaymentRecord[] = (response.items || []).map((entry) => ({
          paymentId: Math.trunc(Number(entry.paymentId)),
          providerPaymentId: String(entry.providerPaymentId || ''),
          amountMinor: Number(entry.amountMinor || 0),
          currency: String(entry.currency || 'RUB'),
          credits: Number(entry.credits || 0),
          createdAtMs: Date.now(),
          source: 'server'
        })).filter((entry) => Number.isFinite(entry.paymentId) && entry.paymentId > 0)
        setPendingPayments((prev) => {
          const local = prev.length ? prev : readPendingPayments()
          const merged = mergePendingPayments(local, serverItems)
          writePendingPayments(merged)
          return merged
        })
      } catch {
        setPendingPayments((prev) => {
          if (prev.length) return prev
          const restored = readPendingPayments()
          writePendingPayments(restored)
          return restored
        })
      }
    }
    void bootstrap()
    return () => { cancelled = true }
  }, [hydrated, isAuthed, mergePendingPayments])

  useEffect(() => {
    if (!hydrated || !isAuthed) return
    void pollPendingPayments()
    const timer = window.setInterval(() => { void pollPendingPayments() }, POLL_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [hydrated, isAuthed, pollPendingPayments])

  useEffect(() => () => {
    if (foreignCardTooltipTimerRef.current) {
      window.clearTimeout(foreignCardTooltipTimerRef.current)
    }
  }, [])

  const closeTopupModal = useCallback(() => {
    setOpened(false)
    setTopupStep('packs')
    setProcessingPaymentId(null)
    setWaitingPaymentStatus('pending')
    setWaitingPaymentRecord(null)
    setLastConfirmationUrl(null)
    setCheckoutLoading(false)
    setCheckoutProvider(null)
    setCheckoutPackCode(null)
    setReceiptEmailError(null)
    setPromoValidating(false)
  }, [])

  const openTopup = useCallback((options?: OpenTopupOptions) => {
    requiredCreditsHintRef.current = missingCreditsForTopup(
      Number(options?.requiredCredits),
      options?.currentBalanceCredits ?? profile?.balance
    )
    setTopupSessionKey((prev) => prev + 1)
    receiptEmailTouchedRef.current = false
    setReceiptEmail(initialReceiptEmail(profile?.email))
    setPacks([])
    setPacksError(null)
    setPacksLoading(false)
    setSelectedPackCode(null)
    setPromoCode('')
    setPromoError(null)
    setPromoValidation(null)
    setPromoValidating(false)
    setCheckoutLoading(false)
    setCheckoutProvider(null)
    setCheckoutPackCode(null)
    setReceiptEmailError(null)
    setTopupStep('packs')
    setProcessingPaymentId(null)
    setWaitingPaymentStatus('pending')
    setWaitingPaymentRecord(null)
    setLastConfirmationUrl(null)
    setReferralTopupStatus(null)
    setTelegramRewardStatus(null)
    setOpened(true)
    void loadPacks()
    void loadTopupBonusEligibility()
  }, [loadPacks, loadTopupBonusEligibility, profile?.balance, profile?.email])

  const contextValue = useMemo<BillingFlowContextValue>(() => ({
    openTopup,
    pendingPaymentsCount: pendingPayments.length
  }), [openTopup, pendingPayments.length])

  const topupModalSize = topupStep === 'packs' ? 1400 : 560
  const topupModalTitle = topupStep === 'packs'
    ? <Title order={2}>Покупка кредитов</Title>
    : topupStep === 'checkout'
      ? <Title order={2}>Подтверждение оплаты</Title>
      : null

  return (
    <BillingFlowContext.Provider value={contextValue}>
      {children}

      <Modal
        key={topupSessionKey}
        opened={opened}
        onClose={closeTopupModal}
        title={topupModalTitle}
        size={topupModalSize}
        centered
        padding={topupStep === 'packs' ? 'lg' : 'xl'}
      >
        <Stack gap="md">
          {topupStep === 'packs' && (
            <>
              {showTelegramBonusHint && (
                <div className="billing-channel-bonus-hint">
                  <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                    <ThemeIcon size={34} radius="xl" color="blue" variant="light">
                      <IconGift size={18} />
                    </ThemeIcon>
                    <div style={{ minWidth: 0 }}>
                      <Text fw={800} size="sm" lh={1.15}>Бесплатная генерация за подписку</Text>
                      <Text size="xs" c="dimmed" lh={1.25}>
                        Привяжите Telegram и подтвердите подписку на канал, начислим {telegramBonusCreditsLabel} кредитов.
                      </Text>
                    </div>
                  </Group>
                  <Button
                    size="xs"
                    radius="xl"
                    variant="light"
                    onClick={() => {
                      setOpened(false)
                      navigate('/profile?tab=programs')
                    }}
                  >
                    Получить
                  </Button>
                </div>
              )}
              {packsLoading && !packs.length ? (
                <Group justify="center" py="md"><Loader size="sm" /></Group>
              ) : null}
              {packsError && <Alert color="red" mb="sm">{packsError}</Alert>}
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md" pt={18}>
                {packs.map((pack) => {
                  const marketingBadge = getPackMarketingBadge(pack.code)
                  return (
                    <Card
                      key={pack.code}
                      withBorder
                      radius="lg"
                      p="lg"
                      style={{
                        borderColor: 'var(--mantine-color-gray-3)',
                        boxShadow: '0 4px 14px rgba(0,0,0,.04)',
                        background: '#fff',
                        position: 'relative',
                        overflow: 'visible'
                      }}
                    >
                      {marketingBadge ? (
                        <div
                          style={{
                            position: 'absolute',
                            top: -16,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: marketingBadge.background,
                            color: '#fff',
                            borderRadius: 999,
                            padding: '6px 12px',
                            fontSize: 12,
                            fontWeight: 800,
                            letterSpacing: '.02em',
                            boxShadow: '0 4px 10px rgba(0,0,0,.14)',
                            zIndex: 1
                          }}
                        >
                          {marketingBadge.label}
                        </div>
                      ) : null}
                        <Stack gap="sm" style={{ minHeight: 320 }}>
                        <Stack gap={8} style={{ minHeight: 186 }}>
                          <Stack gap={2}>
                            <Text fw={800} size="1.75rem" lh={1.1}>{pack.title}</Text>
                            <Text c="dimmed" size="md">{getPackDescription(pack.code)}</Text>
                          </Stack>
                          <Stack gap={3}>
                            <Group gap={8} align="flex-end" wrap="nowrap">
                              <Text fw={900} size="2rem" lh={1}>{formatCurrency(pack.priceMinor, pack.currency)}</Text>
                              {Number(pack.oldPriceMinor || 0) > Number(pack.priceMinor || 0) ? (
                                <Text size="xl" c="dimmed" td="line-through">
                                  {formatCurrency(Number(pack.oldPriceMinor || 0), pack.currency)}
                                </Text>
                              ) : (
                                <Text size="xl" style={{ visibility: 'hidden' }}>-</Text>
                              )}
                            </Group>
                          </Stack>
                          <Group gap={8} align="center">
                            <ThemeIcon size={22} radius="xl" color="blue" variant="light">
                              <IconSparkles size={14} />
                            </ThemeIcon>
                            <Text fw={700} size="xl">{Math.round(pack.credits)} кредитов</Text>
                          </Group>
                        </Stack>
                        <Divider my={0} />
                        <Stack gap={6} mt="auto">
                          <Group gap={8} wrap="nowrap">
                            <ThemeIcon size={18} radius="xl" color="teal" variant="light"><IconPhoto size={12} /></ThemeIcon>
                            <Text size="lg">{Math.max(1, Math.ceil(pack.credits / 30))} изображений</Text>
                          </Group>
                          <Group gap={8} wrap="nowrap">
                            <ThemeIcon size={18} radius="xl" color="violet" variant="light"><IconVideo size={12} /></ThemeIcon>
                            <Text size="lg">{Math.max(1, Math.ceil(pack.credits / 60))} видео</Text>
                          </Group>
                        </Stack>
                        <Stack gap={6} mt="md">
                          <Button
                            fullWidth
                            radius="xl"
                            variant="filled"
                            disabled={checkoutLoading}
                            onClick={() => {
                              setSelectedPackCode(pack.code)
                              setTopupStep('checkout')
                            }}
                          >
                            Пополнить
                          </Button>

                        </Stack>
                      </Stack>
                    </Card>
                  )
                })}
              </SimpleGrid>
            </>
          )}

          {topupStep === 'checkout' && (
            <Stack gap="md">
              <Stack gap={8}>
                <Group justify="space-between">
                  <Text c="dimmed" size="lg">Пакет:</Text>
                  <Text fw={700} size="xl">{selectedPack?.title || '—'}</Text>
                </Group>
                <Group justify="space-between">
                  <Text c="dimmed" size="lg">Кредиты:</Text>
                  <Text fw={700} size="xl">{Math.round(Number(selectedPack?.credits || 0))}</Text>
                </Group>
              </Stack>

              <Stack gap={6}>
                <Text fw={700} size="lg">Промокод</Text>
                <Group align="flex-start" gap={8} wrap="nowrap">
                  <TextInput
                    value={promoCode}
                    onChange={(event) => {
                      setPromoCode(event.currentTarget.value)
                      if (promoError) setPromoError(null)
                    }}
                    placeholder="Код или ссылка"
                    size="md"
                    style={{ flex: 1 }}
                    error={promoError || undefined}
                  />
                  <ActionIcon
                    size={42}
                    radius="md"
                    variant="light"
                    mt={1}
                    onClick={() => { void applyPromo() }}
                    loading={promoValidating}
                    disabled={!promoCode.trim()}
                    aria-label="Применить промокод"
                  >
                    <IconArrowRight size={18} />
                  </ActionIcon>
                </Group>
              </Stack>

              <Divider />

              <Stack gap={6}>
                {savingsMinor > 0 ? (
                  <Group justify="space-between">
                    <Text c="dimmed" size="lg">Скидка:</Text>
                    <Text fw={700} size="lg">{formatCurrency(savingsMinor, selectedPack?.currency || 'RUB')}</Text>
                  </Group>
                ) : null}
                <Group justify="space-between">
                  <Text c="dimmed" size="lg">Итого:</Text>
                  <Text fw={900} size="1.85rem">{formatCurrency(finalPriceMinor, selectedPack?.currency || 'RUB')}</Text>
                </Group>
              </Stack>

              <Stack gap={6}>
                <TextInput
                  label="Email для чека"
                  placeholder="name@example.com"
                  value={receiptEmail}
                  onChange={(event) => {
                    receiptEmailTouchedRef.current = true
                    setReceiptEmail(event.currentTarget.value)
                    if (receiptEmailError) setReceiptEmailError(null)
                  }}
                  error={receiptEmailError || undefined}
                  size="md"
                  inputMode="email"
                  autoComplete="email"
                  required
                />
              </Stack>

              <Stack gap={6}>
                <Tooltip
                  label={paymentDisabledReason}
                  opened={Boolean(paymentDisabledReason) && foreignCardTooltipTarget === 'checkout-payment'}
                  disabled={!paymentDisabledReason}
                  withArrow
                >
                  <Button
                    size="lg"
                    radius="xl"
                    onClick={() => {
                      if (paymentDisabledReason) {
                        showForeignCardDisabledTooltip('checkout-payment')
                        return
                      }
                      void submitCheckout('yookassa')
                    }}
                    onMouseEnter={() => {
                      if (paymentDisabledReason) setForeignCardTooltipTarget('checkout-payment')
                    }}
                    onMouseLeave={() => {
                      if (paymentDisabledReason) hideForeignCardDisabledTooltip('checkout-payment')
                    }}
                    loading={!paymentDisabledReason && checkoutLoading && checkoutProvider === 'yookassa'}
                    disabled={!selectedPack || (checkoutLoading && checkoutProvider !== 'yookassa')}
                    aria-disabled={Boolean(paymentDisabledReason) || undefined}
                    style={paymentDisabledReason ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                    fullWidth
                  >
                    Оплатить
                  </Button>
                </Tooltip>
                <Tooltip
                  label={foreignCardDisabledReason}
                  opened={Boolean(foreignCardDisabledReason) && foreignCardTooltipTarget === 'checkout'}
                  disabled={!foreignCardDisabledReason}
                  withArrow
                >
                  <Button
                    size="lg"
                    radius="xl"
                    variant="default"
                    leftSection={<IconCreditCard size={18} />}
                    onClick={() => {
                      if (foreignCardDisabledReason) {
                        showForeignCardDisabledTooltip('checkout')
                        return
                      }
                      void submitCheckout('robokassa')
                    }}
                    onMouseEnter={() => {
                      if (foreignCardDisabledReason) setForeignCardTooltipTarget('checkout')
                    }}
                    onMouseLeave={() => {
                      if (foreignCardDisabledReason) hideForeignCardDisabledTooltip('checkout')
                    }}
                    loading={!foreignCardDisabledReason && checkoutLoading && checkoutProvider === 'robokassa'}
                    disabled={!selectedPack || (checkoutLoading && checkoutProvider !== 'robokassa')}
                    aria-disabled={Boolean(foreignCardDisabledReason) || undefined}
                    style={foreignCardDisabledReason ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                    fullWidth
                  >
                    Иностранной картой
                  </Button>
                </Tooltip>
              </Stack>
            </Stack>
          )}

          {topupStep === 'waiting' && (
            <Stack gap="md" align="center" py={4}>
              <ThemeIcon
                size={56}
                radius={56}
                color={waitingPaymentStatus === 'pending' ? 'indigo' : 'red'}
                variant="light"
              >
                {waitingPaymentStatus === 'pending' ? <Loader size={28} /> : <IconAlertCircle size={28} />}
              </ThemeIcon>
              <Stack gap={4} align="center">
                {waitingPaymentStatus === 'pending' ? (
                  <>
                    <Title order={3} ta="center">Ожидаем подтверждение оплаты</Title>
                    <Text c="dimmed" ta="center" size="md">
                      Мы следим за статусом платежа. Кредиты начислятся автоматически.
                    </Text>
                  </>
                ) : (
                  <>
                    <Title order={3} ta="center">Оплата не завершена</Title>
                    <Text c="dimmed" ta="center" size="md">
                      {waitingPaymentStatus === 'canceled'
                        ? 'Платеж был отменен. Вы можете повторить оплату.'
                        : 'Не удалось завершить платеж. Попробуйте снова.'}
                    </Text>
                  </>
                )}
              </Stack>
              {(waitingPaymentRecord || processingPayment) && (
                <Stack gap={6} style={{ width: '100%' }}>
                  <Group justify="space-between">
                    <Text c="dimmed" size="md">Платеж</Text>
                    <Text fw={700} size="lg">#{(waitingPaymentRecord || processingPayment)?.paymentId}</Text>
                  </Group>
                  <Group justify="space-between">
                    <Text c="dimmed" size="md">Сумма</Text>
                    <Text fw={700} size="lg">
                      {formatCurrency(
                        Number((waitingPaymentRecord || processingPayment)?.amountMinor || 0),
                        (waitingPaymentRecord || processingPayment)?.currency || 'RUB'
                      )}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Text c="dimmed" size="md">Кредиты</Text>
                    <Text fw={700} size="lg">{Math.round(Number((waitingPaymentRecord || processingPayment)?.credits || 0))}</Text>
                  </Group>
                </Stack>
              )}
              {waitingPaymentStatus === 'pending' && lastConfirmationUrl && (
                <Button
                  leftSection={<IconExternalLink size={16} />}
                  onClick={() => { if (lastConfirmationUrl) openConfirmationInNewTab(lastConfirmationUrl) }}
                >
                  Открыть оплату
                </Button>
              )}
              {waitingPaymentStatus !== 'pending' && (
                <Button
                  onClick={() => {
                    setWaitingPaymentStatus('pending')
                    setTopupStep('checkout')
                  }}
                >
                  Попробовать снова
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </Modal>

      <Modal
        opened={!!successPayment}
        onClose={closeSuccessModal}
        title={<Title order={2}>Оплата</Title>}
        centered
        size={680}
      >
        <Stack gap="lg" align="center" py="md">
          <ThemeIcon
            size={84}
            radius={84}
            color="teal"
            variant="light"
            style={{ boxShadow: '0 10px 28px rgba(20,184,166,.22)' }}
          >
            <IconCheck size={40} />
          </ThemeIcon>
          <Stack gap={4} align="center">
            <Text fw={900} size="xl" ta="center">Оплата прошла</Text>
            <Text c="dimmed" ta="center" size="sm">
              Кредиты зачислены на баланс.
            </Text>
          </Stack>
          <Card withBorder radius="md" p="md" style={{ width: '100%' }}>
            <SimpleGrid cols={3} spacing="xs">
              <Stack gap={2} align="center">
                <Text c="dimmed" size="xs">Платеж</Text>
                <Text fw={700} size="sm">#{successPayment?.paymentId}</Text>
              </Stack>
              <Stack gap={2} align="center">
                <Text c="dimmed" size="xs">Сумма</Text>
                <Text fw={800}>{formatCurrency(Number(successPayment?.amountMinor || 0), successPayment?.currency || 'RUB')}</Text>
              </Stack>
              <Stack gap={2} align="center">
                <Text c="dimmed" size="xs">Кредиты</Text>
                <Text fw={800}>{Math.round(Number(successPayment?.credits || 0))}</Text>
              </Stack>
            </SimpleGrid>
          </Card>
          <Button fullWidth radius="xl" onClick={closeSuccessModal}>
            Продолжить
          </Button>
          <Button
            fullWidth
            radius="xl"
            variant="default"
            onClick={() => {
              closeSuccessModal()
              navigate('/profile?tab=transactions')
            }}
          >
            В профиль
          </Button>
        </Stack>
      </Modal>
    </BillingFlowContext.Provider>
  )
}

export function useBillingFlow() {
  const ctx = useContext(BillingFlowContext)
  if (!ctx) throw new Error('useBillingFlow must be used within BillingFlowProvider')
  return ctx
}
